// src/components/scheduler/CalendarPreview.tsx
import React, { useState } from 'react';

interface CalendarPreviewProps {
    currentDate?: Date;
}

export const CalendarPreview: React.FC<CalendarPreviewProps> = ({
    currentDate = new Date()
}) => {
    const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('12h');

    // Get timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Calculate the start of the current week (Monday)
    const getWeekStart = (date: Date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        return new Date(d.setDate(diff));
    };

    // Get next 3 business days AFTER today
    const getNextBusinessDays = (startDate: Date, count: number = 3) => {
        const businessDays: Date[] = [];
        let currentDate = new Date(startDate);
        // Start from tomorrow
        currentDate.setDate(currentDate.getDate() + 1);

        while (businessDays.length < count) {
            // Skip weekends
            if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
                businessDays.push(new Date(currentDate));
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return businessDays;
    };

    // Generate calendar days for display
    const generateCalendarDays = () => {
        const weekStart = getWeekStart(currentDate);
        const weeks: Date[][] = [];
        let currentWeek: Date[] = [];

        // Generate two weeks to ensure we show next 3 business days
        for (let i = 0; i < 14; i++) {
            const date = new Date(weekStart);
            date.setDate(weekStart.getDate() + i);

            currentWeek.push(date);

            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
        }

        return weeks;
    };

    const weeks = generateCalendarDays();
    const nextBusinessDays = getNextBusinessDays(currentDate);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const currentMonth = monthNames[currentDate.getMonth()];
    const currentYear = currentDate.getFullYear();

    // Check if date is one of the next 3 business days
    const isNextBusinessDay = (date: Date) => {
        return nextBusinessDays.some(bd =>
            bd.getDate() === date.getDate() &&
            bd.getMonth() === date.getMonth() &&
            bd.getFullYear() === date.getFullYear()
        );
    };

    // Check if date is today
    const isToday = (date: Date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    // Check if date is weekend
    const isWeekend = (date: Date) => {
        return date.getDay() === 0 || date.getDay() === 6;
    };

    // Sample time slots
    const sampleTimes = timeFormat === '12h'
        ? ['09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM']
        : ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];

    return (
        <div className="opacity-50 pointer-events-none">
            {/* Month and Year Header */}
            <div className="text-center mb-4">
                <h3 className="text-lg font-medium text-gray-700">{currentMonth} {currentYear}</h3>
            </div>

            {/* Calendar Grid */}
            <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4">
                {/* Day headers */}
                <div className="grid grid-cols-5 gap-2 mb-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(day => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar days */}
                {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="grid grid-cols-5 gap-2 mb-2">
                        {week.filter(date => !isWeekend(date)).map((date, dayIndex) => {
                            const isAvailable = isNextBusinessDay(date);
                            const today = isToday(date);

                            return (
                                <div
                                    key={dayIndex}
                                    className={`
                                        aspect-square flex flex-col items-center justify-center relative rounded-md text-sm
                                        ${isAvailable ? 'bg-gray-100 border-2 border-gray-300' : 'bg-gray-50'}
                                        ${today ? 'font-bold' : ''}
                                    `}
                                >
                                    <span className="text-gray-600">{date.getDate()}</span>
                                    {today && (
                                        <span className="absolute bottom-1 w-1 h-1 bg-gray-400 rounded-full"></span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Timezone and Time Format */}
            <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Timezone</span>
                    <span className="font-medium text-gray-700">{timezone}</span>
                </div>

                <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Time format</span>
                    <div className="flex items-center space-x-2">
                        <button
                            className={`px-3 py-1 text-xs rounded ${timeFormat === '12h'
                                    ? 'bg-gray-300 text-gray-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                        >
                            12h
                        </button>
                        <button
                            className={`px-3 py-1 text-xs rounded ${timeFormat === '24h'
                                    ? 'bg-gray-300 text-gray-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                        >
                            24h
                        </button>
                    </div>
                </div>

                {/* Sample time slots */}
                <div>
                    <p className="text-sm text-gray-600 mb-2">Available times</p>
                    <div className="grid grid-cols-4 gap-2">
                        {sampleTimes.slice(0, 8).map((time, index) => (
                            <div
                                key={index}
                                className="bg-gray-100 text-gray-500 px-2 py-1 rounded text-xs text-center"
                            >
                                {time}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};